define(function(require, exports, module) {
    'use strict';

    var $ = require('jquery');
    var _ = require('underscore');
    var Deferred = require('deferred');
    var urlUtil = require('util/url.util');
    var dispatcher = require('bundle').dispatcher;

    /**
     * @typedef {Object} jBone
     * @alias $
     * @property {Function} Deferred
     * @property {Function} Event
     * @property {Function} Widget
     */

    /**
     * Calculates offset from parent element (defaults to `body`)
     * @type {Function}
     * @return {{top: number, left: number}}
     */
    $.fn.offset = function() {
        var childRect = this[0].getBoundingClientRect();

        return {
            top: childRect.top + window.scrollY,
            left: childRect.left + window.scrollX
        };
    };

    $.fn.isInViewport = function() {
        return this.isInViewportByPercentage(1);
    };

    /**
     * Check if part (percentage) of element is in viewport
     * @type {Function}
     * @param percentage value between 0 and 1 (e.g. 0.5)
     */
    $.fn.isInViewportByPercentage = function(percentage) {
        const el = this[0];
        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        return !(
            Math.floor(100 - (rect.top >= 0 ? 0 : rect.top) / -rect.height * 100) < 100 * percentage ||
            Math.floor(100 - (rect.bottom - windowHeight) / rect.height * 100) < 100 * percentage
        );
    };

    $.fn.removeClasses = function(regexp) {
        this.forEach(function(el) {
            Array.prototype.slice.call(el.classList)
                .forEach(function(className) {
                    className && regexp.test(className) && el.classList.remove(className);
                }, this);
        });

        return this;
    };

    $.cookie = function(name, value, options) {
        var matches, exp, date, updatedCookie, propName, propValue, allCookie;

        if (arguments.length === 1) {
            matches = document.cookie.match(
                new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\\/+^])/g, '\\$1') + '=([^;]*)'));
            return matches ? decodeURIComponent(matches[1]) : undefined;
        }
        if (arguments.length > 1) {
            options = options || {};
            exp = options.expires;

            if (typeof exp === 'number') {
                date = new Date();
                date.setTime(date.getTime() + exp * 1000);
                exp = options.expires = date;
            }

            if (exp && exp.toUTCString) {
                options.expires = exp.toUTCString();
            }

            value = options.raw ? value : encodeURIComponent(value);
            updatedCookie = name + '=' + value;
            for (propName in options) {
                updatedCookie += '; ' + propName;
                propValue = options[propName];
                if (propValue !== true) {
                    updatedCookie += '=' + propValue;
                }
            }
            document.cookie = updatedCookie;
            dispatcher.dispatch('document:cookie:changed');
        } else {
            if (document.cookie.length && document.cookie.includes('=')) {
                allCookie = Object.create(null);

                document.cookie.split(';').forEach(function(item) {
                    var splittedItem = item.split('=');

                    allCookie[splittedItem[0].trim()] = splittedItem[1];
                });

                return allCookie;
            }
        }
    };

    $.removeCookie = function(name) {
        $.cookie(name, null, {expires: -1});
    };

    $.ajax = function(options) {
        var xhr;
        var response;
        var parseResponse = function(responseText) {
            if (options.dataType === 'json') {
                try {
                    response = JSON.parse(responseText);
                } catch (err) {
                    return options.error({
                        errorCode: -2,
                        err: err,
                        message: 'Data from server can\'t be parsed',
                        originResponse: responseText
                    });
                }
            } else {
                response = responseText;
            }

            return options.success(response, xhr);
        };

        _.defaults(options, {type: 'get'});

        if (options.data instanceof Object) {
            if (options.type === 'get') {
                options.url += '?' + urlUtil.serialize(options.data);
            } else {
                options.data = options.data instanceof FormData ? options.data : urlUtil.serialize(options.data);
            }
        }

        if (!options.url.includes(location.protocol) || !window.XDomainRequest
            || window.XDomainRequest && window.atob) { // detect IE9

            xhr = new XMLHttpRequest();

            xhr.open(options.type, options.url, true);

            // enable CORS support for cross domain cookies
            if (_.isObject(options.xhrFields) && options.xhrFields.withCredentials === true) {
                xhr.withCredentials = true;
            }

            if (!['get', 'options'].includes(options.type.toLowerCase())) {
                if (options.contentType) {
                    xhr.setRequestHeader('Content-Type', options.contentType);
                } else if (!(options.data instanceof FormData)) {
                    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
                }
            }

            if (options.headers instanceof Object) {
                for (var i in options.headers) {
                    xhr.setRequestHeader(i, options.headers[i]);
                }
            }

            if (options.password && options.username) {
                xhr.setRequestHeader('Authorization', 'Basic ' + btoa(options.username + ':' + options.password));
            }

            xhr.onreadystatechange = function() {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        parseResponse(xhr.responseText);
                    } else {
                        options.error(xhr.statusText || null, xhr.status ? 'error' : 'abort', xhr.responseText);
                    }
                }
            };

            xhr.send(options.data);
        } else {
            xhr = new window.XDomainRequest();

            xhr.open(options.type, options.url);
            xhr.onerror = function() {
                options.error(null, 'error');
            };

            xhr.onload = function() {
                if (options.dataType === 'json') {
                    response = JSON.parse(xhr.responseText);
                } else {
                    response = xhr.responseText;
                }

                options.success(response, xhr);
            };

            if (options.data instanceof Object) {
                options.data = urlUtil.serialize(options.data);
            }

            // dirty hacks for IE9
            xhr.onprogress = _.noop;
            xhr.ontimeout = _.noop;

            xhr.send(options.data);
        }

        return xhr;
    };

    $.getScript = (function() {
        var loadedScripts = [];
        var head;
        var script;
        var dfd;

        return function(url, callback) {
            dfd = this.Deferred();
            callback = callback || function() {};

            if (!loadedScripts.includes(url)) {
                head = document.getElementsByTagName('head')[0];
                script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = url;
                loadedScripts.push(url);

                script.onload = dfd.resolve;
                script.onerror = dfd.reject;

                head.appendChild(script);
            } else {
                dfd.resolve();
            }

            dfd.done(callback);
            return dfd.promise();
        };
    })();

    $.getDocHeight = function() {
        return _.max([
            document.body.scrollHeight,
            document.body.offsetHeight,
            document.body.clientHeight,
            document.documentElement.scrollHeight,
            document.documentElement.offsetHeight,
            document.documentElement.clientHeight
        ]);
    };

    /**
     * Calculates offset from parent element (defaults to `body`)
     * @type {Function}
     * @return {{top: number, left: number}}
     */
    $.fn.offset = function() {
        var childRect = this[0].getBoundingClientRect();

        return {
            top: childRect.top + window.scrollY,
            left: childRect.left + window.scrollX
        };
    };

    $.fn.hide = function() {
        this.data('displayTypeBeforeHide', this.css('display'));
        this.css('display', 'none');
        return this;
    };

    $.fn.show = function() {
        this.css('display', this.data('displayTypeBeforeHide') || 'block');
        this.removeData('displayTypeBeforeHide');
        return this;
    };

    $.fn.parents = function(selector, excludeSelf) {
        var result = [];
        var i, node;

        for (i = 0; i < this.length; i++) {
            node = this[i];

            if (excludeSelf) {
                node = node.parentNode;
            }

            while (node && node !== document) {
                if (node.matches(selector)) {
                    result.push(node);
                    break;
                }
                node = node.parentNode;
            }

        }

        return $(result);
    };

    /**
     * @deprecated Does not comply to jBone interface convention
     */
    $.fn.prepend = function(child) {
        this[0].insertBefore(child, this[0].firstChild);
    };

    /**
     * @deprecated Do not use
     */
    $.fn.moveBefore = function(elementB) {
        if (elementB instanceof $) {
            elementB = elementB[0];
        }
        var elementA = this[0];
        var elementBParent = elementB.parentNode;
        var elementBPlaceholder = document.createElement('div');

        elementBParent.insertBefore(elementBPlaceholder, elementB);
        elementBParent.replaceChild(elementA, elementBPlaceholder);
    };

    /**
     * @deprecated Do not use
     */
    $.fn.moveAfter = function(elementB) {
        var elementA = this[0];
        if (elementB instanceof $) {
            elementB = elementB[0];
        }
        var elementBParent = elementB.parentNode;
        var elementBPlaceholder = document.createElement('div');

        if (elementB.nextSibling) {
            elementBParent.insertBefore(elementBPlaceholder, elementB.nextSibling);
        } else {
            elementBParent.appendChild(elementBPlaceholder);
        }

        elementBParent.replaceChild(elementA, elementBPlaceholder);
    };

    /**
     * @deprecated Do not use
     */
    $.fn.swapWith = function(elementB) {
        var elementA = this[0];
        if (elementB instanceof $) {
            elementB = elementB[0];
        }

        var elementAParent = elementA.parentNode;
        var elementBParent = elementB.parentNode;
        var elementAPlaceholder = document.createElement('div');

        elementAParent.insertBefore(elementAPlaceholder, elementA);
        elementBParent.insertBefore(elementA, elementB);
        elementAParent.insertBefore(elementB, elementAPlaceholder);

        elementAParent.removeChild(elementAPlaceholder);
    };

    $.fn.index = function() {
        var el = this[0];
        var index = 0;

        while (el = el.previousSibling) {
            if (el.nodeType !== Node.TEXT_NODE || !/^\s*$/.test(el.data)) {
                index++;
            }
        }
        return index;
    };

    Deferred.installInto($);

    module.exports = $;
});
